import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Kit 2 React themes
import theme from "assets/theme";
import Home from "pages/Home";
import SignIn from "pages/LandingPages/SignIn";
import SignUp from "pages/LandingPages/SignUp";
import UserInfo from "pages/UserInfo";
import Clients from "pages/Clients";
import Payments from "pages/Payments";
import FAQs from "pages/FAQs";
import ManageAccount from "pages/ManageAccount";

// Material Kit 2 React routes
import routes from "routes";
import { getRoutes as getNavRoutes } from "routes";
import i18next from "i18next";
import { clearAuthStorage, isAuthenticated } from "utils/auth";
import { persistMarketingParamsFromSearch } from "utils/marketing";

function RequireAuth({ children }) {
  const location = useLocation();
  const authed = isAuthenticated();

  // If the session cannot be recovered, bounce to sign-in and remember where the user wanted to go.
  if (!authed) {
    clearAuthStorage();
    return <Navigate to="/signin/" replace state={{ from: location }} />;
  }

  return children;
}
RequireAuth.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function App() {
  const location = useLocation();
  const { pathname, hash, search } = location;
  const navigate = useNavigate();

  const [lang, setLang] = useState(() => i18next.resolvedLanguage || i18next.language);

  useEffect(() => {
    const handler = (lng) => setLang(lng);
    i18next.on("languageChanged", handler);
    return () => i18next.off("languageChanged", handler);
  }, []);

  // If your `routes.js` builds labels based on language, compute it here.
  // Falls back to the existing static `routes` array if `getNavRoutes` is not needed.
  const routesWithLang = useMemo(() => {
    try {
      const built = getNavRoutes(lang);
      return Array.isArray(built) ? built : routes;
    } catch (e) {
      return routes;
    }
  }, [lang]);

  // Hash-aware scroll behavior:
  // - If URL has #id, scroll to that element after render.
  // - Otherwise, default to scroll-to-top on route changes.
  useEffect(() => {
    if (hash && String(hash).startsWith("#")) {
      const rawId = String(hash).slice(1);
      const id = rawId.endsWith("/") ? rawId.slice(0, -1) : rawId;

      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return true;
        }
        return false;
      };

      // Try immediately, then next frame, then shortly after.
      if (tryScroll()) return;
      requestAnimationFrame(() => {
        if (tryScroll()) return;
        setTimeout(() => {
          tryScroll();
        }, 150);
      });
      return;
    }

    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname, hash]);

  useEffect(() => {
    // Capture ref/promo from landing and signup URLs, then persist for later signup submit.
    const isCapturePath = pathname === "/" || pathname.startsWith("/signup");
    if (isCapturePath) {
      persistMarketingParamsFromSearch(search);
    }
  }, [pathname, search]);

  useEffect(() => {
    // If auth cannot be recovered on protected routes, force sign-in immediately.
    const isProtectedPayment = pathname === "/payment" || pathname === "/payment/";
    const isProtected = pathname.startsWith("/userinfo") || isProtectedPayment;
    if (isProtected && !isAuthenticated()) {
      clearAuthStorage();
      navigate("/signin/", { replace: true, state: { from: { pathname, hash } } });
    }
  }, [pathname, hash, navigate]);

  const buildRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return buildRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {buildRoutes(routesWithLang)}
        <Route path="/signin/" element={<SignIn />} />
        <Route path="/signup/" element={<SignUp />} />
        <Route
          path="/userinfo/"
          element={
            <RequireAuth>
              <UserInfo />
            </RequireAuth>
          }
        />
        <Route
          path="/payment/"
          element={
            <RequireAuth>
              <Payments />
            </RequireAuth>
          }
        />
        <Route path="/payment/public/" element={<Payments />} />
        <Route path="/manage-account/:base64urlsafe_username/" element={<ManageAccount />} />
        <Route path="/manage-account/:base64urlsafe_username" element={<ManageAccount />} />
        <Route path="/faq/" element={<FAQs />} />
        <Route path="/clients/" element={<Clients />} />
        <Route path="/client/:platform/" element={<Clients />} />
        <Route path="/" element={<Home />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </ThemeProvider>
  );
}
